/** Plans and limits (backend domain/plans.py): showing and editing plan values. Pure. */
import type { S } from "../api/client";
import type { MessageKey } from "../i18n/keys.ts";
import { fmtLux, fmtMoneyShort, parseLux, type LuxWords } from "./lux.ts";

export type PlanItem = S["PlanItem"];
export type Tier = "unverified" | "domain" | "business";
export const TIERS: readonly Tier[] = ["unverified", "domain", "business"];
export const TIER_LABEL: Record<Tier, string> = { unverified: "Unverified", domain: "Domain-verified", business: "Business-verified" };

/** A plan value: a number, a flag, null (unlimited, talk to us, contract…) or Free values by verification tier. */
export type PlanValue = number | boolean | null | Partial<Record<Tier, number | null>>;

export function isTiered(value: unknown): value is Partial<Record<Tier, number | null>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function money(c: number): string {
  return `$${(c / 100).toLocaleString("en-US", { minimumFractionDigits: c % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
}

/** The catalogue's words for "no value" (backend domain/plans.py `none_label`), in the `lux` catalog. */
const NONE_KEYS: Record<string, MessageKey> = {
  unlimited: "lux.planValue.none.unlimited",
  "talk to us": "lux.planValue.none.talkToUs",
  "volume price": "lux.planValue.none.volumePrice",
  "not offered": "lux.planValue.none.notOffered",
  "no discount": "lux.planValue.none.noDiscount",
  contract: "lux.planValue.none.contract",
  "12 months": "lux.planValue.none.twelveMonths",
  off: "lux.planValue.none.off",
  "platform default": "lux.planValue.none.platformDefault",
  "not sold beyond the included uses": "lux.planValue.none.notSoldUses",
  "not sold beyond the included sessions": "lux.planValue.none.notSoldSessions",
};

/** A catalogue's `none_label` in the reader's language (as it is when the catalog does not know it). */
export function noneLabel(label: string, w?: LuxWords): string {
  const key = NONE_KEYS[label];
  return w && key ? w.t(key) : label;
}

/** How a value reads in a table: "$149", "25", "30 days", "on", "unlimited", "U 10 · D 20 · B 30". */
export function formatValue(item: Pick<PlanItem, "type" | "none_label">, value: unknown, w?: LuxWords): string {
  if (isTiered(value)) {
    return TIERS.filter((t) => t in value)
      .map((t) => `${t[0]!.toUpperCase()} ${formatValue(item, value[t] ?? null, w)}`)
      .join(" · ");
  }
  if (w) return wordsValue(item, value, w);
  if (value === null || value === undefined) return item.none_label;
  if (item.type === "flag") return value ? "on" : "off";
  if (typeof value !== "number") return String(value);
  if (item.type === "cents") return money(value);
  if (item.type === "lux") return `${fmtLux(value)} Lux`;
  if (item.type === "days") return `${value.toLocaleString("en-US")} days`;
  if (item.type === "percent") return `${value}%`;
  if (item.type === "priority") return `${value <= 10 ? "high" : value <= 50 ? "standard" : "low"} (${value})`;
  return value.toLocaleString("en-US");
}

function wordsValue(item: Pick<PlanItem, "type" | "none_label">, value: unknown, w: LuxWords): string {
  const { t, locale } = w;
  if (value === null || value === undefined) return noneLabel(item.none_label, w);
  if (item.type === "flag") return value ? t("lux.planValue.on") : t("lux.planValue.off");
  if (typeof value !== "number") return String(value);
  if (item.type === "cents") return fmtMoneyShort(value, "USD", locale);
  if (item.type === "lux") return t("lux.amount", { amount: fmtLux(value, locale) });
  if (item.type === "days") return t("lux.planValue.days", { count: value });
  if (item.type === "percent") return new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 2 }).format(value / 100);
  if (item.type === "priority") return t("lux.planValue.priority", { level: value <= 10 ? "high" : value <= 50 ? "standard" : "low", value });
  return value.toLocaleString(locale);
}

/** The text of an input for a scalar value (empty = null). Prices are edited in dollars. */
export function toInput(item: Pick<PlanItem, "type">, value: number | boolean | null | undefined): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "on" : "off";
  if (item.type === "lux") return fmtLux(value).replace(/,/g, "");
  return item.type === "cents" ? (value / 100).toFixed(2).replace(/\.00$/, "") : String(value);
}

/** Parses an input's text: empty = null, otherwise a whole number from 0 (prices in dollars → cents). Undefined: invalid. */
export function fromInput(item: Pick<PlanItem, "type">, text: string): number | boolean | null | undefined {
  const t = text.trim().toLowerCase();
  if (item.type === "flag") return t === "on" || t === "true" ? true : t === "off" || t === "false" ? false : undefined;
  if (t === "" || t === "unlimited" || t === "-") return null;
  if (item.type === "lux") return parseLux(t.replace(/\s*lux$/, ""));
  if (item.type === "cents") {
    if (!/^\d+(\.\d{1,2})?$/.test(t.replace(/^\$/, ""))) return undefined;
    return Math.round(Number(t.replace(/^\$/, "")) * 100);
  }
  if (!/^\d+$/.test(t.replace(/,/g, ""))) return undefined;
  return Number(t.replace(/,/g, ""));
}

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** The values of `draft` that differ from `saved`, ready for PATCH `values`, and the keys back on their default
 * (`reset`) when the draft equals the default. */
export function planChanges(
  saved: Record<string, unknown>,
  draft: Record<string, unknown>,
  defaults: Record<string, unknown>,
): { values: Record<string, unknown>; reset: string[] } {
  const values: Record<string, unknown> = {};
  const reset: string[] = [];
  for (const key of Object.keys(draft)) {
    if (same(draft[key], saved[key])) continue;
    if (same(draft[key], defaults[key])) reset.push(key);
    else values[key] = draft[key];
  }
  return { values, reset };
}

/** Groups catalogue items by area, keeping their order. */
export function byArea<T extends { area: string }>(items: readonly T[]): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const item of items) groups.set(item.area, [...(groups.get(item.area) ?? []), item]);
  return [...groups.entries()];
}

/** Share of the month's allowance used, 0–1 (0 when there is none). */
export function allowanceShare(used: number, allowance: number): number {
  return allowance > 0 ? Math.min(1, Math.max(0, used / allowance)) : 0;
}

/** "Unlimited" or "3 of 6" for a used/limit pair. */
export function ofLimit(used: number, limit: number | null | undefined, none = "unlimited", w?: LuxWords): string {
  if (w) return limit === null || limit === undefined ? w.t("lux.planValue.usedNone", { used, none: noneLabel(none, w) }) : w.t("lux.planValue.usedOf", { used, limit });
  return limit === null || limit === undefined ? `${used} (${none})` : `${used} of ${limit}`;
}

/** The landing page link of the "Powered by Solar OS" badge, as the backend builds it (for previews). */
export function badgeUrl(website: string, ref: string): string {
  const q = new URLSearchParams({ ref, utm_source: "widget", utm_medium: "badge", utm_campaign: "powered_by" });
  return `${website.replace(/\/+$/, "")}/?${q.toString()}`;
}

/** A company's enforcement switch of a limit: as the plan, enforced, or not enforced. */
export type Enforcement = "inherit" | "on" | "off";

export function enforcementOf(switches: Record<string, boolean>, key: string): Enforcement {
  return key in switches ? (switches[key] ? "off" : "on") : "inherit";
}

/** The PATCH `disable` / `enable` lists that turn the saved switched-off keys into the draft's. */
export function switchChanges(saved: readonly string[], draft: readonly string[]): { disable: string[]; enable: string[] } {
  return { disable: draft.filter((k) => !saved.includes(k)), enable: saved.filter((k) => !draft.includes(k)) };
}

/** "Free +3, Pro +10 and Business +25": each plan's monthly switch bonus (design Lux) from the catalogue, in the
 * reader's list style; plans without one (by contract) are left out. Live test LT-94: the admin page quoted old
 * numbers. */
export function switchBonuses(plans: readonly Pick<S["Plan"], "name" | "values">[], locale: string): string {
  const parts = plans.flatMap((p) => {
    const v = p.values.switch_bonus_designs;
    return typeof v === "number" && v > 0 ? [`${p.name} +${v}`] : [];
  });
  return new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(parts);
}
